import type { GrantPublic } from '@accounts/shared';

/**
 * A PERSON'S STATE ON ONE SUITE APP — the FINAL demo's `appStatusFor`, from the reads wave 1
 * serves, in its order: a live grant wins, then a pending request on a catalog row bound to
 * the app, then a revoked grant, else nothing. One answer for the Matrix's Suite apps
 * columns and the person page's glance, so the two can never disagree.
 *
 * The demo's "Revoked, waiting for the app" needs the app-deactivation outbox (spec row 8),
 * which has no backend in wave 1, so a revoked grant reads as Revoked.
 */
export type AppStatusKey = 'granted' | 'requested' | 'revoked' | 'none';

/** A pending request on an app-bound catalog row. */
export interface PendingAppRequest {
  userId: string;
  applicationId: string;
}

export type AppStatusIndex = ReadonlyMap<string, AppStatusKey>;

const RANK: Record<AppStatusKey, number> = { none: 0, revoked: 1, requested: 2, granted: 3 };

const key = (userId: string, applicationId: string): string => `${userId}|${applicationId}`;

export function indexAppStatus(
  grants: readonly GrantPublic[],
  pending: readonly PendingAppRequest[],
): AppStatusIndex {
  const index = new Map<string, AppStatusKey>();
  const raise = (at: string, status: AppStatusKey) => {
    if (RANK[status] > RANK[index.get(at) ?? 'none']) index.set(at, status);
  };
  for (const grant of grants) {
    raise(key(grant.userId, grant.applicationId), grant.revokedAt === null ? 'granted' : 'revoked');
  }
  for (const request of pending) raise(key(request.userId, request.applicationId), 'requested');
  return index;
}

export function appStatusAt(
  index: AppStatusIndex,
  userId: string,
  applicationId: string,
): AppStatusKey {
  return index.get(key(userId, applicationId)) ?? 'none';
}
